import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AssessmentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [phase, setPhase] = useState('verifying'); // verifying | failed | redirecting
  const [errMsg, setErrMsg] = useState('');
  // Each checkout session owns its request, retry timer and delayed redirect.
  useEffect(() => {
    document.title = 'Verifying your purchase…';
    setPhase('verifying');
    setErrMsg('');
    if (!sessionId) {
      setPhase('failed');
      setErrMsg('Missing session id.');
      return;
    }
    let attempts = 0;
    let active = true;
    let timeoutId;
    const controller = new AbortController();
    const poll = async () => {
      if (!active) return;
      attempts += 1;
      try {
        const r = await fetch(`${BACKEND}/api/assessment/checkout-status/${encodeURIComponent(sessionId)}`, {
          signal: controller.signal,
        });
        if (!active) return;
        if (r.ok) {
          const d = await r.json();
          if (!active) return;
          if (d.paid && d.result_token) {
            setPhase('redirecting');
            timeoutId = window.setTimeout(() => {
              if (active) navigate(`/assessment/start?token=${d.result_token}`, { replace: true });
            }, 600);
            return;
          }
        }
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        // Transient network/server error during polling — log for debugging and keep polling.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[assessment-success] poll attempt failed, will retry:', err?.message);
        }
      }
      if (attempts >= 25) {
        setPhase('failed');
        setErrMsg("We couldn't verify the payment yet. Stripe sometimes takes a moment — please refresh in 30 seconds, or contact us if it persists.");
        return;
      }
      timeoutId = window.setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
      controller.abort();
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0a0d0e] flex items-center justify-center px-5" data-testid="assessment-success">
      <div className="max-w-md w-full bg-white border border-[#E5E7E7] rounded-2xl p-8 text-center shadow-[0_24px_64px_rgba(0,76,84,0.10)]">
        {phase === 'verifying' && (
          <>
            <Loader2 className="h-10 w-10 text-[#004C54] animate-spin mx-auto" />
            <h1 className="font-heading text-xl font-bold tracking-tight mt-4">Verifying your payment…</h1>
            <p className="text-sm text-foreground/65 mt-2">Hang tight — Stripe is confirming the charge. This usually takes a few seconds.</p>
          </>
        )}
        {phase === 'redirecting' && (
          <>
            <CheckCircle2 className="h-10 w-10 text-[#4FBCC7] mx-auto" />
            <h1 className="font-heading text-xl font-bold tracking-tight mt-4">You&rsquo;re in.</h1>
            <p className="text-sm text-foreground/65 mt-2">Opening your assessment…</p>
          </>
        )}
        {phase === 'failed' && (
          <>
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
            <h1 className="font-heading text-xl font-bold tracking-tight mt-4">Still verifying…</h1>
            <p className="text-sm text-foreground/65 mt-2" data-testid="assessment-success-error">{errMsg}</p>
            <Button onClick={() => window.location.reload()} className="mt-4 bg-[#004C54] hover:bg-[#003a40]">Try again</Button>
          </>
        )}
      </div>
    </div>
  );
}
